import { ArticleStatus, ArticleType } from '@atlas/types'
import { z } from 'zod'

/** A classification dimension selection: axis code → selected value codes. */
const articleClassificationSchema = z.record(z.string(), z.array(z.string()))

// Locale code from the `locale` classification axis. Axis codes use an
// underscore separator (e.g. 'es_es', 'us_en'); a hyphen ('es-es') is also
// accepted. Matches the shape the message schema already accepts.
const languageIso = z.string().regex(/^[a-z]{2}[-_][a-z]{2}$/i, 'languageIso must be a locale code, e.g. "en_us"')

/**
 * A URL-friendly handle: lowercase alphanumerics separated by single hyphens.
 * Optional on create — omitted, it is derived from the title server-side.
 */
const slug = z
	.string()
	.min(1)
	.max(120)
	.regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug must be lowercase alphanumerics separated by single hyphens')

/**
 * The body is long-form prose, so the cap is two orders of magnitude above the
 * message cap — but it is still a cap. The translation flow sends the whole
 * body to a billed model call for EVERY requested locale, so an unbounded body
 * makes one authorized request an unbounded-cost vector. 200k characters is
 * roughly a 60-page document, far beyond any real article.
 */
const MAX_CONTENT_LENGTH = 200_000

/**
 * How many images one article may own. Client-supplied and every entry is a
 * fetch a reader's browser will make, so it is capped rather than unbounded.
 * Generous for an illustrated brand-details article.
 */
const MAX_IMAGE_COUNT = 50

/**
 * Create an Article (HTTP POST /articles body).
 *
 * `articleId`, `tenantId`, `status`, `publishedAt`, `authorUserId`,
 * `translationGroupId`, `isSource`, `translationStatus`,
 * `translationFailureReason`, `isActive` and every timestamp are server-owned
 * — `.strict()` rejects them (422) rather than silently stripping them, so a
 * client that believes it published an article is told otherwise instead of
 * finding a draft later. Status moves only through the transition endpoint.
 */
export const articleCreateSchema = z
	.object({
		title: z.string().min(1).max(300),
		slug: slug.optional(),
		summary: z.string().max(500).nullable().optional(),
		// Empty is valid: a brand-new article opens as an untouched canvas and the
		// composer autosaves before a word is written.
		content: z.string().max(MAX_CONTENT_LENGTH, `Content is too long (max ${MAX_CONTENT_LENGTH} chars)`).default(''),
		// What the article is for, and therefore which surface shows it. Defaults to
		// `general`; the brand template sets `brand_details`.
		articleType: z.enum(ArticleType).default(ArticleType.GENERAL),
		// The `file-service` file ids this article owns, in the author's order. Not
		// validated against the file table: file identity is owned elsewhere, and a
		// deleted file degrades to a missing image rather than blocking a save.
		imageFileIds: z
			.array(z.uuid())
			.max(MAX_IMAGE_COUNT, `An article cannot hold more than ${MAX_IMAGE_COUNT} images`)
			.default([]),
		languageIso,
		classification: articleClassificationSchema.optional(),
	})
	.strict()

/**
 * Update an Article (HTTP PUT /articles/{id} body). Every field is optional,
 * but at least one must be present. `summary` is `.nullable().optional()` so
 * omitting preserves and an explicit `null` clears; `title`, `content`,
 * `languageIso`, `articleType` and `imageFileIds` are not nullable because
 * their columns are `NOT NULL`. Passing `imageFileIds` REPLACES the list, so a
 * caller reorders or removes by sending the list it wants.
 *
 * A `slug` is only changed when passed explicitly: it is a stable handle, and
 * re-deriving it from an edited title would break any link already shared.
 */
export const articleUpdateSchema = articleCreateSchema
	// `content`, `articleType` and `imageFileIds` carry `.default()` on create,
	// which `.partial()` would not make optional — re-declared here without one.
	.omit({ content: true, articleType: true, imageFileIds: true })
	.extend({
		content: z.string().max(MAX_CONTENT_LENGTH, `Content is too long (max ${MAX_CONTENT_LENGTH} chars)`),
		articleType: z.enum(ArticleType),
		imageFileIds: z.array(z.uuid()).max(MAX_IMAGE_COUNT, `An article cannot hold more than ${MAX_IMAGE_COUNT} images`),
	})
	.partial()
	.strict()
	.refine((data) => Object.keys(data).length > 0, {
		message: 'At least one field must be provided',
	})

/** Transition an Article's lifecycle status (HTTP POST /articles/{id}/transition body). */
export const articleTransitionSchema = z
	.object({
		to: z.enum(ArticleStatus),
	})
	.strict()

/**
 * Every locale fans out to its own worker invocation and its own billed model
 * call over a long-form document, so an uncapped array is an unbounded-cost
 * vector for one authorized request. The caps here are materially lower than the
 * message endpoint's for exactly that reason: an article pair costs one to two
 * orders of magnitude more than a message pair.
 */
const MAX_LOCALES_PER_ARTICLE = 20
const MAX_BATCH_ITEMS = 10
const MAX_BATCH_TRANSLATION_PAIRS = 50

const targetLocales = z
	.array(languageIso)
	.min(1)
	.max(MAX_LOCALES_PER_ARTICLE, `Cannot request more than ${MAX_LOCALES_PER_ARTICLE} locales for one article`)

/**
 * Request an AI translation of a source article into one or more target locales
 * (POST /articles/{id}/translations). Each locale creates, or resets to pending,
 * a target-locale row in the source's translation group.
 */
export const articleRequestTranslationSchema = z
	.object({
		targetLocales,
	})
	.strict()

/**
 * Request AI translation for many articles in one call (POST
 * /articles/translations/batch), each item with its own target-locale set and
 * each processed independently server-side.
 *
 * The two per-dimension caps bound each axis but NOT their product: 10 items x
 * 20 locales would still let one request queue 200 minutes-long model calls.
 * The `.refine` below bounds the combined total directly, rather than relying on
 * the product of two independently-chosen numbers to stay small by coincidence.
 */
export const articleRequestTranslationBatchSchema = z
	.object({
		items: z
			.array(
				z
					.object({
						articleId: z.uuid(),
						targetLocales,
					})
					.strict(),
			)
			.min(1)
			.max(MAX_BATCH_ITEMS, `Cannot request translation for more than ${MAX_BATCH_ITEMS} articles in one call`)
			.refine(
				(items) => items.reduce((sum, item) => sum + item.targetLocales.length, 0) <= MAX_BATCH_TRANSLATION_PAIRS,
				`Cannot request more than ${MAX_BATCH_TRANSLATION_PAIRS} total (article, locale) translations in one batch call`,
			),
	})
	.strict()

export type ArticleCreateInput = z.infer<typeof articleCreateSchema>
export type ArticleUpdateInput = z.infer<typeof articleUpdateSchema>
export type ArticleTransitionInput = z.infer<typeof articleTransitionSchema>
export type ArticleRequestTranslationInput = z.infer<typeof articleRequestTranslationSchema>
export type ArticleRequestTranslationBatchInput = z.infer<typeof articleRequestTranslationBatchSchema>
